import React, { useContext, useEffect, useState, useCallback, useRef } from "react";
import { classNames } from "../../helpers/classNames";
import SocketContext, { useSocket } from "../socket/SocketProvider";
import { fetchProfileDetails, updateProfileService } from "../../service/profileService";
import { useDispatch, useSelector } from "react-redux";
import { setConsultantSwitch } from "../../store/slice/switchSlice";
import { getProfileDetails } from "../../service/authService";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import { unlockRingtoneFromGesture, bindRingtoneUnlock } from "../../util/ringtone";
import {
  AUTO_ONLINE_EVENT,
  getAutoOnlineOnSignIn,
  persistAutoOnlineOnSignIn,
  setAutoOnlineOnSignIn,
} from "../../util/consultantNotes";
import {
  isManualOffline,
  setManualOffline,
  broadcastPresence,
  subscribePresence,
  consumeSignInAutoOnline,
  clearSignInAutoOnline,
} from "../../util/consultantPresence";
import PreCallDeviceCheck from "./PreCallDeviceCheck";
import { stopAllRegisteredMedia } from "../../util/mediaTrackRegistry";
import {
  setConsultantLoginReadinessPending,
  takeLoginDeviceProbe,
  profileOffersModes,
  hasUsableFreshDeviceReadiness,
  autoProbeInitialDeviceReadiness,
  saveDeviceReadiness,
} from "../../util/deviceReadiness";

const busyLang = {
  en: "Busy",
  it: "Occupato",
  es: "Ocupado",
};
const onlineLang = {
  en: "Online",
  it: "In linea",
  es: "En línea",
};
const offlineLang = {
  en: "Offline",
  it: "Non in linea",
  es: "Desconectado",
};
const setOnlineLang = {
  en: "Tap to go Offline",
  it: "Tocca per andare offline",
  es: "Toca para desconectarte",
};
const setOfflineLang = {
  en: "Tap to go Online",
  it: "Tocca per andare online",
  es: "Toca para conectarte",
};
const autoLabel = {
  en: "Auto Online when I sign in",
  it: "Online automatico al accesso",
  es: "Online automático al iniciar sesión",
};
const autoHint = {
  en: "Only applies when you log in — not on page refresh. If you go Offline, you stay Offline until you tap Online.",
  it: "Si applica solo al login, non al refresh. Se vai Offline, resti offline finché non tocchi Online.",
  es: "Solo al iniciar sesión, no al refrescar. Si pasas a Offline, sigues offline hasta tocar Online",
};
const readinessLang = {
  title: { en: "Available Modes", it: "Modalità disponibili", es: "Modos disponibles" },
  chat: { en: "Chat", it: "Chat", es: "Chat" },
  audio: { en: "Audio", it: "Audio", es: "Audio" },
  video: { en: "Video", it: "Video", es: "Video" },
  ready: { en: "Ready", it: "Pronto", es: "Listo" },
  available: { en: "Available", it: "Disponibile", es: "Disponible" },
  unavailable: { en: "Unavailable", it: "Non disponibile", es: "No disponible" },
  notReady: { en: "Not ready", it: "Non pronto", es: "No listo" },
  hint: {
    en: "Device readiness determines which consultation modes are available to clients.",
    it: "La disponibilità del dispositivo determina quali modalità di consultazione sono disponibili per i clienti.",
    es: "La preparación del dispositivo determina qué modos de consulta están disponibles para los clientes.",
  },
  test: { en: "Test devices", it: "Testa dispositivi", es: "Probar dispositivi" },
};

/**
 * @param {"default"|"large"} size
 * @param {boolean} showAutoPref
 */
const ProfileOnlineButton = ({
  data,
  id,
  size = "default",
  showAutoPref = false,
}) => {
  const lang = useSelector((state) => state?.languageSlice?.data);
  const language = localStorage.getItem("language") || lang || "en";
  const [status, setStatus] = useState(() => (isManualOffline() ? "offline" : "online"));
  const [profileData, setProfileData] = useState(null);
  const profileDataRef = useRef(profileData);
  const [, setLoaded] = useState(true);
  const token = localStorage.getItem("auth_token") || Cookies.get("auth_token");
  const dispatch = useDispatch();
  const socket = useSocket();
  const { connectSocket } = useContext(SocketContext);
  const [loading, setLoading] = useState(false);
  const [autoOnline, setAutoOnline] = useState(() => getAutoOnlineOnSignIn());
  const [showDeviceCheck, setShowDeviceCheck] = useState(false);
  // Call readiness — determined accurately from Profile settings + verified fresh Device Check.
  const [readyForAudio, setReadyForAudio] = useState(false);
  const [readyForVideo, setReadyForVideo] = useState(false);
  const [readinessCheckMode, setReadinessCheckMode] = useState(null); // "voice" | "video" | null
  const [offeredModes, setOfferedModes] = useState({ chat: true, voice: false, video: false });

  const initialProbeDoneRef = useRef(false);
  const isProbingRef = useRef(false);

  const reduxProfileData = useSelector((state) => state?.profileSlice?.data);

  useEffect(() => {
    profileDataRef.current = profileData;
  }, [profileData]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("prontopsy_device_fallback_warning");
      if (raw) {
        sessionStorage.removeItem("prontopsy_device_fallback_warning");
        const warnings = JSON.parse(raw);
        if (Array.isArray(warnings)) {
          warnings.forEach((msg) => {
            toast.warn(msg, { autoClose: 6000 });
          });
        }
      }
    } catch (_) { }
  }, []);

  const evaluateAndSyncReadiness = useCallback(async (forcedSocket = null, explicitProfile = null) => {
    let activeProfile = explicitProfile || profileDataRef.current;
    if (!activeProfile) {
      try {
        activeProfile = JSON.parse(localStorage.getItem("userDetails") || "null");
      } catch (_) { }
    }
    if (!activeProfile) return;

    const offers = profileOffersModes(activeProfile);
    setOfferedModes(offers);

    if (!offers.voice && !offers.video) {
      setReadyForAudio(false);
      setReadyForVideo(false);
      return;
    }

    let audioUsable = offers.voice ? await hasUsableFreshDeviceReadiness("voice") : false;
    let videoUsable = offers.video ? await hasUsableFreshDeviceReadiness("video") : false;

    // Check login probe if available
    const loginProbe = takeLoginDeviceProbe();
    if (loginProbe) {
      if (offers.voice && loginProbe.audio && !audioUsable) {
        audioUsable = true;
      }
      if (offers.video && loginProbe.video && !videoUsable) {
        videoUsable = true;
      }
      if (audioUsable || videoUsable) {
        saveDeviceReadiness({
          audio: audioUsable,
          video: videoUsable,
        });
      }
    }

    // Auto-probe on initial use / fresh session / first login:
    // If the consultant offers audio/video, but readiness is not yet established
    // (e.g. fresh Incognito session or first login where permissions are "prompt" and no cache exists),
    // automatically run device probe so readiness is detected on first login without requiring
    // manual VERIFY action.
    const needsAudioProbe = offers.voice && !audioUsable;
    const needsVideoProbe = offers.video && !videoUsable;

    if ((needsAudioProbe || needsVideoProbe) && !initialProbeDoneRef.current && !isProbingRef.current) {
      isProbingRef.current = true;
      try {
        const probeResult = await autoProbeInitialDeviceReadiness({
          audio: needsAudioProbe,
          video: needsVideoProbe,
        });
        if (probeResult?.audio && offers.voice) {
          audioUsable = true;
        }
        if (probeResult?.video && offers.video) {
          videoUsable = true;
        }
        initialProbeDoneRef.current = true;
        setConsultantLoginReadinessPending(false);
      } catch (_) {
        initialProbeDoneRef.current = true;
      } finally {
        isProbingRef.current = false;
      }
    }

    setReadyForAudio(audioUsable);
    setReadyForVideo(videoUsable);

    const activeSocket = forcedSocket || socket;
    if (activeSocket?.connected) {
      try {
        activeSocket.emit("consultantSetReadiness", {
          readyForAudio: audioUsable,
          readyForVideo: videoUsable,
        });
      } catch (_) { }
    }

    const prevAudio = activeProfile.readyForAudio === true;
    const prevVideo = activeProfile.readyForVideo === true;
    if (audioUsable !== prevAudio || videoUsable !== prevVideo) {
      try {
        const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
        ud.readyForAudio = audioUsable;
        ud.readyForVideo = videoUsable;
        localStorage.setItem("userDetails", JSON.stringify(ud));
      } catch (_) { }
      try {
        updateProfileService({
          readyForAudio: audioUsable,
          readyForVideo: videoUsable,
        });
      } catch (_) { }
    }
  }, [socket]);

  useEffect(() => {
    if (reduxProfileData) {
      setProfileData(reduxProfileData);
      evaluateAndSyncReadiness(null, reduxProfileData);
    }
  }, [reduxProfileData, evaluateAndSyncReadiness]);

  useEffect(() => {
    evaluateAndSyncReadiness();
    const interval = setInterval(evaluateAndSyncReadiness, 4000);

    // Instant dynamic detection on hardware plug / unplug
    const handleDeviceChange = () => {
      evaluateAndSyncReadiness();
    };
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener("devicechange", handleDeviceChange);
    }

    // Instant dynamic detection on browser permission changes
    let micStatus = null;
    let camStatus = null;
    const handlePermChange = () => {
      evaluateAndSyncReadiness();
    };

    if (navigator.permissions?.query) {
      navigator.permissions.query({ name: "microphone" }).then((status) => {
        micStatus = status;
        micStatus?.addEventListener?.("change", handlePermChange);
      }).catch(() => {});
      navigator.permissions.query({ name: "camera" }).then((status) => {
        camStatus = status;
        camStatus?.addEventListener?.("change", handlePermChange);
      }).catch(() => {});
    }

    return () => {
      clearInterval(interval);
      if (navigator.mediaDevices?.removeEventListener) {
        navigator.mediaDevices.removeEventListener("devicechange", handleDeviceChange);
      }
      micStatus?.removeEventListener?.("change", handlePermChange);
      camStatus?.removeEventListener?.("change", handlePermChange);
    };
  }, [evaluateAndSyncReadiness]);


  const applyStatus = (next) => {
    setStatus(next);
    if (next === "online" || next === "offline" || next === "busy") {
      broadcastPresence(next);
    }
  };

  // Keep all instances (sidebar + dashboard) in sync
  useEffect(() => {
    return subscribePresence((s) => {
      if (!s) return;
      if (s === "busy" || s === "online" || s === "offline") {
        setStatus(s);
      }
    });
  }, []);

  useEffect(() => {
    const onPref = (e) => {
      if (typeof e?.detail?.enabled === "boolean") setAutoOnline(e.detail.enabled);
    };
    window.addEventListener(AUTO_ONLINE_EVENT, onPref);
    return () => window.removeEventListener(AUTO_ONLINE_EVENT, onPref);
  }, []);

  // Load true status from server once. Do NOT force online on refresh.
  useEffect(() => {
    if (!id) return;
    fetchProfileDetails(id, setProfileData, setLoaded).then((res) => {
      if (res?.status !== 200) return;
      const serverStatus = res?.docs?.onlineStatus || "offline";
      // Keep last saved readiness across login. A failed/skipped login
      // probe must not turn Audio/Video Call Now off (Auto Online is
      // independent of these toggles).
      if (typeof res?.docs?.autoOnlineOnSignIn === "boolean") {
        setAutoOnline(res.docs.autoOnlineOnSignIn);
        setAutoOnlineOnSignIn(res.docs.autoOnlineOnSignIn);
      }

      evaluateAndSyncReadiness(null, res?.docs);

      // Fresh sign-in with Auto Online checked: do not treat a stale
      // server "offline" as an intentional Offline tap.
      const justSignedInOnline = consumeSignInAutoOnline();
      if (justSignedInOnline) {
        setManualOffline(false);
        applyStatus("online");
        if (socket?.connected) {
          socket.emit("userOnline", id);
          evaluateAndSyncReadiness(socket, res?.docs);
        }
        return;
      }

      // User intentionally offline — re-assert offline on server (socket connect may have raced)
      if (isManualOffline()) {
        applyStatus("offline");
        if (socket?.connected) {
          socket.emit("userOffline", id);
        }
        return;
      }

      if (serverStatus === "busy") {
        applyStatus("busy");
        return;
      }

      // Consultant is intentionally Online (or signed in with Auto Online)
      applyStatus("online");
      if (socket?.connected) {
        socket.emit("userOnline", id);
        evaluateAndSyncReadiness(socket, res?.docs);
      }
      try {
        const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
        ud.onlineStatus = "online";
        localStorage.setItem("userDetails", JSON.stringify(ud));
      } catch (_) { }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // When socket connects later, re-apply intentional offline or re-assert online + sync readiness
  useEffect(() => {
    if (!socket || !id) return;
    const onConnect = () => {
      if (isManualOffline()) {
        socket.emit("userOffline", id);
        setStatus("offline");
      } else {
        socket.emit("userOnline", id);
      }
      evaluateAndSyncReadiness(socket);
    };
    socket.on("connect", onConnect);
    if (socket.connected) {
      if (isManualOffline()) {
        socket.emit("userOffline", id);
      } else {
        socket.emit("userOnline", id);
      }
      evaluateAndSyncReadiness(socket);
    }
    return () => {
      socket.off("connect", onConnect);
    };
  }, [socket, id, evaluateAndSyncReadiness]);

useSocket(
  "userStatusResponse",
  (info) => {
    if (!info || String(info.userId) !== String(id)) return;

    // Intentional offline: ignore spurious online events from other tabs/sockets
    if (isManualOffline()) {
      setStatus("offline");
      if (info.online === true && socket?.connected) {
        socket.emit("userOffline", id);
      }
      return;
    }

    if (info.onlineStatus === "busy" || info.busy === true) {
      setStatus("busy");
    } else if (info.online === true || info.onlineStatus === "online") {
      setStatus((prev) => (prev === "busy" ? prev : "online"));
    } else if (info.online === false) {
      // Active consultant is logged in and chose Online. Re-assert online.
      setStatus((prev) => (prev === "busy" ? prev : "online"));
      if (socket?.connected) {
        socket.emit("userOnline", id);
      }
    }
  },
  [id, socket],
);

useSocket(
  "consultantBusyResponse",
  (data) => {
    if (!data || String(data.consultantId) !== String(id)) return;
    if (isManualOffline()) {
      setStatus("offline");
      return;
    }
    applyStatus(data?.isBusy ? "busy" : "online");
  },
  [id],
);

// Keep readiness in sync across this consultant's other open tabs/devices.
useSocket(
  "consultantReadinessResponse",
  (data) => {
    if (!data || String(data.userId) !== String(id)) return;
    if (typeof data.readyForAudio === "boolean") setReadyForAudio(data.readyForAudio);
    if (typeof data.readyForVideo === "boolean") setReadyForVideo(data.readyForVideo);
  },
  [id],
);

const emitReadiness = (next) => {
  try {
    socket?.emit("consultantSetReadiness", next);
  } catch (_) {
    /* ignore */
  }
};

// Removed manual Audio/Video toggles because readiness is determined automatically.

useSocket("consultantJoinResponse", async () => {
  setTimeout(() => {
    if (isManualOffline()) {
      setStatus("offline");
      return;
    }
    if (id) {
      fetchProfileDetails(id, setProfileData, setLoaded).then((res) => {
        if (res?.status === 200) {
          applyStatus(res?.docs?.onlineStatus || "offline");
          evaluateAndSyncReadiness(null, res?.docs);
        }
      }).catch(() => {});
    }
  }, 1000);
});

useSocket(
  "chatJoinConfirmResponse",
  async () => {
    setTimeout(() => {
      if (isManualOffline()) {
        setStatus("offline");
        return;
      }
      if (id) {
        fetchProfileDetails(id, setProfileData, setLoaded).then((res) => {
          if (res?.status === 200) {
            applyStatus(res?.docs?.onlineStatus || "offline");
            evaluateAndSyncReadiness(null, res?.docs);
          }
        }).catch(() => {});
      }
    }, 1000);
  },
  [],
);

useEffect(() => {
  bindRingtoneUnlock();
}, []);

// No first-login "choose your availability" prompt anymore — the server
// already decides the correct status at login time (Online by default,
// Offline only if that was the consultant's last explicit choice, Busy if
// mid-call). This component just reflects and lets them change it.
useEffect(() => {
  setConsultantLoginReadinessPending(false);
}, []);

const commitStatusChange = () => {
  unlockRingtoneFromGesture();
  if (!socket || !socket?.connected) connectSocket();
  setLoading(true);

  getProfileDetails(token)
    .then((response) => {
      setLoading(false);
      if (response?.data?.status !== 200) return;
      dispatch(setConsultantSwitch({ status: true }));

      const serverBusy = response?.data?.docs?.onlineStatus === "busy";
      if (serverBusy || status === "busy") {
        applyStatus("busy");
        toast.error("Status cannot be changed while you are currently in a call.", {
          autoClose: 3000,
        });
        return;
      }

      const liveSocket = socket;
      // Toggle from current UI status
      if (status === "online") {
        setManualOffline(true);
        clearSignInAutoOnline();
        applyStatus("offline");
        liveSocket?.emit("userOffline", id);
        try {
          updateProfileService({ onlineStatus: "offline" });
        } catch (_) { }
        // Keep profile cache in sync so refresh / DefaultHeader don't re-online
        try {
          const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
          ud.onlineStatus = "offline";
          localStorage.setItem("userDetails", JSON.stringify(ud));
        } catch (_) {
          /* ignore */
        }
        localStorage.removeItem("appointmentData");
      } else {
        setManualOffline(false);
        applyStatus("online");
        liveSocket?.emit("userOnline", id);
        try {
          updateProfileService({ onlineStatus: "online" });
        } catch (_) { }
        try {
          const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
          ud.onlineStatus = "online";
          localStorage.setItem("userDetails", JSON.stringify(ud));
        } catch (_) {
          /* ignore */
        }
      }
    })
    .catch((error) => {
      setLoading(false);
      console.error(error);
    });
};

const handleStatus = () => {
  // Plain Online/Offline toggle — never gated behind a camera/mic check.
  // A consultant who just wants to check appointments can go Online instantly.
  unlockRingtoneFromGesture();
  commitStatusChange();
};

const onToggleAuto = (e) => {
  const on = e.target.checked;
  setAutoOnline(on);
  persistAutoOnlineOnSignIn(on);
  try {
    socket?.emit("consultantSetAutoOnline", { enabled: on });
  } catch (_) {
    /* ignore */
  }
  toast.info(
    on
      ? language === "it"
        ? "Andrai online automaticamente al prossimo accesso (non al refresh)."
        : language === "es"
          ? "Estarás online automáticamente en el próximo inicio de sesión (no al refrescar)."
          : "You will go Online automatically on next sign-in (not on page refresh)."
      : language === "it"
        ? "Resterai offline dopo l'accesso finché non attivi Online."
        : language === "es"
          ? "Seguirás offline tras iniciar sesión hasta activar Online."
          : "You will stay Offline after sign-in until you tap Online.",
    { autoClose: 4000, toastId: "auto-online-pref" },
  );
};

const label1 =
  status === "busy"
    ? busyLang?.[language]
    : status === "online"
      ? onlineLang?.[language]
      : offlineLang?.[language];
const actionLabel =
  status === "online"
    ? setOnlineLang?.[language]
    : status === "offline"
      ? setOfflineLang?.[language]
      : "";

const statusDot = {
  online: "bg-emerald-500",
  busy: "bg-amber-400",
  offline: "bg-rose-500",
  "": "bg-neutral-300",
};
const statusSoft = {
  online: "bg-emerald-50 border-emerald-200 text-emerald-800",
  busy: "bg-amber-50 border-amber-200 text-amber-900",
  offline: "bg-rose-50 border-rose-200 text-rose-800",
  "": "bg-slate-50 border-slate-200 text-slate-600",
};
const toggleOn = {
  online: "bg-emerald-500",
  busy: "bg-amber-400",
  offline: "bg-slate-300",
  "": "bg-slate-300",
};

return (
  <>
    <PreCallDeviceCheck
      open={showDeviceCheck}
      mode={readinessCheckMode || "video"}
      consultantReadiness={true}
      onReady={() => {
        setShowDeviceCheck(false);
        stopAllRegisteredMedia();
        evaluateAndSyncReadiness();
        setReadinessCheckMode(null);
      }}
      onCancel={() => {
        setShowDeviceCheck(false);
        stopAllRegisteredMedia();
        evaluateAndSyncReadiness();
        setReadinessCheckMode(null);
      }}
    />
    <div className="w-full px-3 space-y-4">
      {/* AVAILABILITY Card */}
      <div className="space-y-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
          {language === "it" ? "DISPONIBILITÀ" : language === "es" ? "DISPONIBILIDAD" : "AVAILABILITY"}
        </div>
        {loading ? (
          <div className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 flex items-center justify-center">
            <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-[#002C6B] border-t-transparent" />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleStatus}
            disabled={status === "busy"}
            className={classNames(
              "w-full rounded-2xl border px-4 py-4 flex items-center justify-between transition-all",
              status === "online"
                ? "bg-emerald-50/40 border-emerald-100/80 text-emerald-800"
                : status === "busy"
                  ? "bg-amber-50/40 border-amber-100/80 text-amber-900 cursor-not-allowed opacity-90"
                  : "bg-slate-50/80 border-slate-200/60 text-slate-600 hover:shadow-sm cursor-pointer active:scale-[0.99]",
            )}>
            <div className="flex items-center gap-3.5">
              <span
                className={classNames(
                  "w-3 h-3 rounded-full flex-shrink-0",
                  statusDot[status] || statusDot[""],
                  status === "online" ? "animate-pulse" : "",
                )}
              />
              <div className="text-left">
                <span className="block text-base font-bold leading-tight">{label1 || "—"}</span>
                {actionLabel ? (
                  <span className="block text-[11px] opacity-70 mt-1 leading-tight">{actionLabel}</span>
                ) : null}
              </div>
            </div>
            {/* iOS-style toggle */}
            <span
              className={classNames(
                "relative w-12 h-6 rounded-full flex-shrink-0 transition-colors duration-200",
                status === "online" ? "bg-emerald-500" : status === "busy" ? "bg-amber-500" : "bg-slate-300",
              )}
              aria-hidden>
              <span
                className={classNames(
                  "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all duration-200",
                  status === "online" || status === "busy" ? "right-0.5 translate-x-0" : "left-0.5 translate-x-0",
                )}
              />
            </span>
          </button>
        )}
      </div>

      {/* CALL READINESS / AVAILABLE MODES Card */}
      {!loading && (
        <div className="w-full rounded-2xl border border-slate-200 bg-white p-4 space-y-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            {readinessLang.title[language] || readinessLang.title.en}
          </div>

          <div className="grid grid-cols-3 gap-2.5">
            {/* Chat Card */}
            <div className={classNames(
              "relative flex flex-col items-center justify-center p-3 rounded-xl border text-center",
              offeredModes.chat
                ? "border-emerald-100 bg-emerald-50/10 text-emerald-800"
                : "border-rose-100 bg-rose-50/10 text-rose-800"
            )}>
              <div className={classNames(
                "absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white shadow-sm border border-white",
                offeredModes.chat ? "bg-emerald-500" : "bg-rose-500"
              )}>
                <i className={classNames("fa-solid", offeredModes.chat ? "fa-check" : "fa-xmark")}></i>
              </div>
              <div className={classNames(
                "w-8 h-8 rounded-full flex items-center justify-center mb-1.5",
                offeredModes.chat ? "bg-emerald-50" : "bg-rose-50"
              )}>
                <i className={classNames("fa-solid fa-comment-dots text-sm", offeredModes.chat ? "text-emerald-600" : "text-rose-500")}></i>
              </div>
              <span className="text-[11px] font-bold">{readinessLang.chat[language] || readinessLang.chat.en}</span>
              <span className={classNames(
                "text-[9px] font-semibold mt-0.5",
                offeredModes.chat ? "text-emerald-600" : "text-rose-600"
              )}>
                {offeredModes.chat
                  ? (readinessLang.available[language] || readinessLang.available.en)
                  : (readinessLang.unavailable[language] || readinessLang.unavailable.en)}
              </span>
            </div>

            {/* Audio Card */}
            <button
              type="button"
              onClick={() => {
                unlockRingtoneFromGesture();
                setReadinessCheckMode("voice");
                setShowDeviceCheck(true);
              }}
              className={classNames(
                "relative flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all cursor-pointer hover:shadow-sm active:scale-[0.98]",
                readyForAudio ? "border-emerald-100 bg-emerald-50/10 text-emerald-800" : "border-rose-100 bg-rose-50/10 text-rose-800"
              )}>
              <div className={classNames(
                "absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white shadow-sm border border-white",
                readyForAudio ? "bg-emerald-500" : "bg-rose-500"
              )}>
                <i className={classNames("fa-solid", readyForAudio ? "fa-check" : "fa-xmark")}></i>
              </div>
              <div className={classNames(
                "w-8 h-8 rounded-full flex items-center justify-center mb-1.5",
                readyForAudio ? "bg-emerald-50" : "bg-rose-50"
              )}>
                <i className={classNames("fa-solid fa-microphone text-sm", readyForAudio ? "text-emerald-600" : "text-rose-500")}></i>
              </div>
              <span className="text-[11px] font-bold">{readinessLang.audio[language] || readinessLang.audio.en}</span>
              <span className={classNames(
                "text-[9px] font-semibold mt-0.5",
                readyForAudio ? "text-emerald-600" : "text-rose-600"
              )}>
                {readyForAudio
                  ? (readinessLang.ready[language] || readinessLang.ready.en)
                  : (readinessLang.notReady[language] || readinessLang.notReady.en)}
              </span>
              {!readyForAudio && (
                <span className="mt-1 px-1.5 py-0.5 rounded bg-rose-500 text-white text-[8px] font-bold uppercase tracking-wider">
                  {language === "it" ? "Verifica" : language === "es" ? "Verificar" : "Verify"}
                </span>
              )}
            </button>

            {/* Video Card */}
            <button
              type="button"
              onClick={() => {
                unlockRingtoneFromGesture();
                setReadinessCheckMode("video");
                setShowDeviceCheck(true);
              }}
              className={classNames(
                "relative flex flex-col items-center justify-center p-3 rounded-xl border text-center transition-all cursor-pointer hover:shadow-sm active:scale-[0.98]",
                readyForVideo ? "border-emerald-100 bg-emerald-50/10 text-emerald-800" : "border-rose-100 bg-rose-50/10 text-rose-800"
              )}>
              <div className={classNames(
                "absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white shadow-sm border border-white",
                readyForVideo ? "bg-emerald-500" : "bg-rose-500"
              )}>
                <i className={classNames("fa-solid", readyForVideo ? "fa-check" : "fa-xmark")}></i>
              </div>
              <div className={classNames(
                "w-8 h-8 rounded-full flex items-center justify-center mb-1.5",
                readyForVideo ? "bg-emerald-50" : "bg-rose-50"
              )}>
                <i className={classNames("fa-solid fa-video text-sm", readyForVideo ? "text-emerald-600" : "text-rose-500")}></i>
              </div>
              <span className="text-[11px] font-bold">{readinessLang.video[language] || readinessLang.video.en}</span>
              <span className={classNames(
                "text-[9px] font-semibold mt-0.5",
                readyForVideo ? "text-emerald-600" : "text-rose-600"
              )}>
                {readyForVideo
                  ? (readinessLang.ready[language] || readinessLang.ready.en)
                  : (readinessLang.notReady[language] || readinessLang.notReady.en)}
              </span>
              {!readyForVideo && (
                <span className="mt-1 px-1.5 py-0.5 rounded bg-rose-500 text-white text-[8px] font-bold uppercase tracking-wider">
                  {language === "it" ? "Verifica" : language === "es" ? "Verificar" : "Verify"}
                </span>
              )}
            </button>
          </div>

          <p className="text-[10px] leading-snug text-slate-400">
            {readinessLang.hint[language] || readinessLang.hint.en}
          </p>

          {status === "offline" && (
            <p className="text-[10px] leading-snug text-amber-700 bg-amber-50/40 p-2 rounded-lg border border-amber-100/50">
              {language === "it"
                ? "Offline: i clienti non possono chiamarti ora, anche se Audio/Video risultano pronti."
                : language === "es"
                  ? "Offline: los clientes no pueden llamarte ahora, aunque Audio/Video estén listos."
                  : "Offline — clients cannot Call Now. Ready flags stay saved but are inactive for clients."}
            </p>
          )}

          <button
            type="button"
            onClick={() => {
              unlockRingtoneFromGesture();
              setReadinessCheckMode("video");
              setShowDeviceCheck(true);
            }}
            className="w-full flex items-center justify-center gap-1.5 text-[11px] font-semibold text-[#002C6B] underline-offset-2 hover:underline py-1 border-t border-slate-100 mt-2">
            <i className="fa-solid fa-desktop text-[10px]"></i>
            {readinessLang.test[language] || readinessLang.test.en}
          </button>
        </div>
      )}

      {showAutoPref && (
        <label className="flex items-start gap-3 cursor-pointer select-none rounded-2xl border border-slate-200/80 bg-slate-50/40 px-3.5 py-3 hover:bg-slate-50 transition-all">
          <input
            type="checkbox"
            className="mt-0.5 h-3.5 w-3.5 accent-[#002C6B] flex-shrink-0"
            checked={autoOnline}
            onChange={onToggleAuto}
          />
          <span className="min-w-0">
            <span className="font-bold text-slate-700 block text-[11px] leading-snug">
              {autoLabel[language] || autoLabel.en}
            </span>
            <span className="text-[10px] text-slate-400 block mt-1 leading-snug">
              {autoHint[language] || autoHint.en}
            </span>
          </span>
        </label>
      )}
    </div>
  </>
);
};

export default ProfileOnlineButton;
